import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { 
  MapPin, Star, Fish, ArrowLeft, Plus, Waves, Car, Coffee, Tent, 
  Phone, Globe, Mail, Calendar, Award, Sparkles, Clock, Users, 
  Ruler, ChevronDown, ChevronUp, Shield, AlertCircle, CheckCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import ProLockedCard from '../components/ProLockedCard';
import BadgeUnlockModal from '../components/BadgeUnlockModal';
import BookingComingSoon from '../components/BookingComingSoon';
import SubmitLakePhotoCTA from '../components/SubmitLakePhotoCTA';
import { resolveFisheryImage } from '../lib/fisheryImages';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const facilityIcons = {
  'Parking': Car,
  'Toilets': Coffee,
  'Bivvy': Tent,
  'Night Fishing': Waves,
  'Showers': Waves,
  'Cafe': Coffee,
  'Tackle Shop': Fish,
  'Bivvy Pegs': Tent,
  'Electric Hookups': Waves,
  'Disabled Access': Users,
  'Camping': Tent,
  'Lodges': Tent,
  'On-site Tackle Shop': Fish,
};

const SESSION_TYPES = [
  { value: 'day', label: 'Day Session', description: 'Dawn to dusk' },
  { value: 'night', label: 'Night Session', description: 'Dusk to dawn' },
  { value: '24hr', label: '24 Hour Session', description: 'Full day & night' },
  { value: '48hr', label: '48 Hour Session', description: 'Two full days' },
];

const LakeDetail = () => {
  const { id } = useParams();
  const { user, token } = useAuth();
  const [lake, setLake] = useState(null);
  const [catches, setCatches] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [catchDialogOpen, setCatchDialogOpen] = useState(false);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [showAllNamedFish, setShowAllNamedFish] = useState(false);
  const [unlockedBadges, setUnlockedBadges] = useState([]);
  
  const [catchForm, setCatchForm] = useState({
    fish_type: '',
    weight: '',
    length: '',
    bait_used: '',
    notes: ''
  });
  
  const [reviewForm, setReviewForm] = useState({
    rating: 5,
    comment: ''
  });

  const [bookingForm, setBookingForm] = useState({
    session_type: 'day',
    date: '',
    number_of_rods: 2,
    notes: ''
  });

  const fishTypes = ['Common Carp', 'Mirror Carp', 'Leather Carp', 'Ghost Carp', 'Koi Carp', 'Grass Carp'];

  useEffect(() => {
    fetchLakeData();
  }, [id]);

  const fetchLakeData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [lakeRes, catchesRes, reviewsRes] = await Promise.all([
        axios.get(`${API}/lakes/${id}`, { headers }),
        axios.get(`${API}/catches?lake_id=${id}`, { headers }),
        axios.get(`${API}/lakes/${id}/reviews`, { headers })
      ]);
      setLake(lakeRes.data);
      setCatches(catchesRes.data);
      setReviews(reviewsRes.data);
    } catch (error) {
      console.error('Failed to fetch lake data:', error);
      toast.error('Failed to load lake data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogCatch = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/catches`, {
        lake_id: id,
        fish_type: catchForm.fish_type,
        weight: parseFloat(catchForm.weight),
        length: catchForm.length ? parseFloat(catchForm.length) : null,
        bait_used: catchForm.bait_used || null,
        notes: catchForm.notes || null
      }, { headers: { Authorization: `Bearer ${token}` } });

      toast.success('Catch logged successfully!');
      if (res.data?.badges_unlocked?.length) {
        setUnlockedBadges(res.data.badges_unlocked);
      }
      setCatchDialogOpen(false);
      setCatchForm({ fish_type: '', weight: '', length: '', bait_used: '', notes: '' });
      fetchLakeData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to log catch');
    }
  };

  const handleAddReview = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/lakes/${id}/reviews`, {
        lake_id: id,
        rating: reviewForm.rating,
        comment: reviewForm.comment || null
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success('Review added successfully!');
      setReviewDialogOpen(false);
      setReviewForm({ rating: 5, comment: '' });
      fetchLakeData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add review');
    }
  };

  const handleBookSession = async (e) => {
    e.preventDefault();
    if (!bookingForm.date) {
      toast.error('Please select a date');
      return;
    }

    setBookingLoading(true);
    try {
      const response = await axios.post(`${API}/bookings`, {
        lake_id: id,
        session_type: bookingForm.session_type,
        date: bookingForm.date,
        number_of_rods: bookingForm.number_of_rods,
        notes: bookingForm.notes,
        origin_url: window.location.origin
      }, { headers: { Authorization: `Bearer ${token}` } });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create booking');
      setBookingLoading(false);
    }
  };

  const getSessionPrice = (sessionType) => {
    if (!lake) return 0;
    switch (sessionType) {
      case 'day': return lake.day_ticket_price || 15;
      case 'night': return lake.night_ticket_price || (lake.day_ticket_price || 15) * 1.5;
      case '24hr': return lake.session_24hr_price || (lake.day_ticket_price || 15) * 2;
      case '48hr': return lake.session_48hr_price || (lake.day_ticket_price || 15) * 3.5;
      default: return lake.day_ticket_price || 15;
    }
  };

  // Get tomorrow's date as minimum booking date
  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#c8b273] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!lake) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <MapPin className="w-16 h-16 text-[#4a504a] mx-auto mb-4" />
          <h2 className="text-xl text-white">Lake not found</h2>
          <Link to="/lakes">
            <Button className="btn-gold mt-4">Back to Lakes</Button>
          </Link>
        </div>
      </div>
    );
  }

  const displayedNamedFish = showAllNamedFish 
    ? lake.named_fish 
    : (lake.named_fish || []).slice(0, 3);

  return (
    <div className="min-h-screen" data-testid="lake-detail">
      {/* Hero */}
      <div 
        className="relative h-80 md:h-96 bg-cover bg-center"
        style={{
          backgroundImage: `url('${resolveFisheryImage(lake, lake.id || lake.name)}')`
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-[#050605] via-[#050605]/60 to-transparent" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(200,178,115,0.12),transparent_60%)] pointer-events-none" />
        <div className="relative z-10 h-full flex flex-col justify-between p-6 md:p-8">
          <Link to="/lakes">
            <Button variant="ghost" className="btn-ghost w-fit">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Lakes
            </Button>
          </Link>
          <div className="max-w-7xl mx-auto w-full">
            <div className="flex flex-wrap gap-2 mb-3">
              {lake.record_weight && (
                <Badge className="bg-[#ff5e00] text-white">
                  <Award className="w-3 h-3 mr-1" />
                  Lake Record: {lake.record_weight}lb
                </Badge>
              )}
              {lake.named_fish?.length > 0 && (
                <Badge className="bg-[#c8b273] text-black">
                  <Sparkles className="w-3 h-3 mr-1" />
                  {lake.named_fish.length} Famous Fish
                </Badge>
              )}
              {lake.region && (
                <Badge variant="outline" className="border-white/20 text-white">
                  {lake.region}
                </Badge>
              )}
            </div>
            <h1 className="font-['Barlow_Condensed'] text-3xl md:text-5xl font-extrabold uppercase tracking-wider text-white">
              {lake.name}
            </h1>
            <p className="text-[#8a948a] mt-2 flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              {lake.location}, {lake.county}
            </p>
          </div>
        </div>
      </div>

      {/* Quick Actions Bar */}
      <div className="sticky top-16 z-20 bg-[#050605]/95 backdrop-blur-sm border-b border-white/5 py-3 px-6 md:px-8">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4 text-sm">
            <span className="flex items-center gap-1 text-[#c8b273]">
              <Star className="w-4 h-4 fill-current" />
              <span className="font-bold">{lake.avg_rating?.toFixed(1) || '0.0'}</span>
              <span className="text-[#8a948a]">({reviews.length} reviews)</span>
            </span>
            {lake.day_ticket_price && (
              <span className="text-white">
                From <span className="font-bold text-[#c8b273]">£{lake.day_ticket_price}</span>/day
              </span>
            )}
          </div>
          {user?.is_admin ? (
            <Dialog open={bookingDialogOpen} onOpenChange={setBookingDialogOpen}>
              <DialogTrigger asChild>
                <Button className="btn-gold" data-testid="book-session-btn">
                  <Calendar className="w-4 h-4 mr-2" />
                  Book Session
                  <span className="ml-2 text-[10px] uppercase tracking-widest text-black/70 border border-black/30 rounded px-1.5 py-0.5">Admin</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#0b0d0b] border-white/10 max-w-md">
              <DialogHeader>
                <DialogTitle className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-wider text-white">
                  Book Your Session
                </DialogTitle>
                <DialogDescription className="text-[#8a948a]">
                  Reserve your spot at {lake.name}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleBookSession} className="space-y-4 mt-4">
                <div>
                  <label className="tactical-label">Session Type</label>
                  <Select 
                    value={bookingForm.session_type} 
                    onValueChange={(v) => setBookingForm(p => ({ ...p, session_type: v }))}
                  >
                    <SelectTrigger className="bg-[#141714] border-white/10 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0b0d0b] border-white/10">
                      {SESSION_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          <div className="flex justify-between items-center w-full">
                            <span>{type.label}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-[#8a948a] mt-1">
                    {SESSION_TYPES.find(t => t.value === bookingForm.session_type)?.description}
                  </p>
                </div>

                <div>
                  <label className="tactical-label">Date</label>
                  <Input
                    type="date"
                    min={getMinDate()}
                    value={bookingForm.date}
                    onChange={(e) => setBookingForm(p => ({ ...p, date: e.target.value }))}
                    className="bg-[#141714] border-white/10 text-white"
                    required
                  />
                </div>

                <div>
                  <label className="tactical-label">Number of Rods</label>
                  <Select 
                    value={String(bookingForm.number_of_rods)} 
                    onValueChange={(v) => setBookingForm(p => ({ ...p, number_of_rods: parseInt(v) }))}
                  >
                    <SelectTrigger className="bg-[#141714] border-white/10 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0b0d0b] border-white/10">
                      <SelectItem value="1">1 Rod</SelectItem>
                      <SelectItem value="2">2 Rods</SelectItem>
                      <SelectItem value="3">3 Rods</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="tactical-label">Special Requests (Optional)</label>
                  <Textarea
                    value={bookingForm.notes}
                    onChange={(e) => setBookingForm(p => ({ ...p, notes: e.target.value }))}
                    className="bg-[#141714] border-white/10 text-white"
                    placeholder="Any special requirements..."
                  />
                </div>

                {/* Price Summary */}
                <div className="bg-[#141714] rounded-lg p-4 border border-white/5">
                  <div className="flex justify-between items-center">
                    <span className="text-[#8a948a]">Session Price</span>
                    <span className="text-2xl font-bold text-[#c8b273]">
                      £{getSessionPrice(bookingForm.session_type).toFixed(2)}
                    </span>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="btn-gold w-full" 
                  disabled={bookingLoading}
                >
                  {bookingLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin mr-2" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Proceed to Payment
                    </>
                  )}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          ) : (
            <BookingComingSoon lakeId={id} lakeName={lake.name} testId="book-session-btn" />
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-6 md:p-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* Quick Stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="glass-card p-4 text-center">
                  <div className="flex items-center justify-center gap-1 text-[#c8b273] mb-1">
                    <Star className="w-5 h-5 fill-current" />
                    <span className="font-['Barlow_Condensed'] text-2xl font-bold">{lake.avg_rating?.toFixed(1) || '0.0'}</span>
                  </div>
                  <p className="data-label text-[#8a948a] text-xs">Rating</p>
                </div>
                <div className="glass-card p-4 text-center">
                  <p className="font-['Barlow_Condensed'] text-2xl font-bold text-white">{lake.catches_count || 0}</p>
                  <p className="data-label text-[#8a948a] text-xs">Catches</p>
                </div>
                <div className="glass-card p-4 text-center">
                  <p className="font-['Barlow_Condensed'] text-2xl font-bold text-white">
                    {lake.lake_size_acres ? `${lake.lake_size_acres}` : '-'}
                  </p>
                  <p className="data-label text-[#8a948a] text-xs">Acres</p>
                </div>
                <div className="glass-card p-4 text-center">
                  <p className="font-['Barlow_Condensed'] text-2xl font-bold text-white">
                    {lake.total_swims || '-'}
                  </p>
                  <p className="data-label text-[#8a948a] text-xs">Swims</p>
                </div>
              </div>

              {/* Tabs for organized content */}
              <Tabs defaultValue="about" className="w-full">
                <TabsList className="w-full bg-[#0b0d0b] border border-white/5">
                  <TabsTrigger value="about" className="flex-1 data-[state=active]:bg-[#c8b273] data-[state=active]:text-black">About</TabsTrigger>
                  <TabsTrigger value="fish" className="flex-1 data-[state=active]:bg-[#c8b273] data-[state=active]:text-black">Fish & Records</TabsTrigger>
                  <TabsTrigger value="catches" className="flex-1 data-[state=active]:bg-[#c8b273] data-[state=active]:text-black">Catches</TabsTrigger>
                </TabsList>

                {/* About Tab */}
                <TabsContent value="about" className="space-y-6 mt-6">
                  {/* Description */}
                  <div className="glass-card p-6">
                    <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-wider text-white mb-4">
                      About This Lake
                    </h2>
                    <p className="text-[#8a948a] leading-relaxed">{lake.description}</p>
                  </div>

                  {/* Facilities */}
                  {lake.facilities?.length > 0 && (
                    <div className="glass-card p-6">
                      <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-wider text-white mb-4">
                        Facilities
                      </h2>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        {lake.facilities.map(facility => {
                          const Icon = facilityIcons[facility] || Waves;
                          return (
                            <div key={facility} className="flex items-center gap-2 text-[#8a948a]">
                              <div className="w-8 h-8 rounded bg-[#141714] flex items-center justify-center">
                                <Icon className="w-4 h-4 text-[#c8b273]" />
                              </div>
                              {facility}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Rules */}
                  {lake.rules?.length > 0 && (
                    <div className="glass-card p-6">
                      <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-wider text-white mb-4 flex items-center gap-2">
                        <Shield className="w-5 h-5 text-[#c8b273]" />
                        Fishery Rules
                      </h2>
                      <ul className="space-y-2">
                        {lake.rules.map((rule, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-[#8a948a]">
                            <AlertCircle className="w-4 h-4 text-[#ff5e00] mt-0.5 flex-shrink-0" />
                            {rule}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Contact & Links */}
                  {(lake.website || lake.phone || lake.email) && (
                    <div className="glass-card p-6">
                      <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-wider text-white mb-4">
                        Contact
                      </h2>
                      <div className="space-y-3">
                        {lake.website && (
                          <a 
                            href={lake.website} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 text-[#c8b273] hover:underline"
                          >
                            <Globe className="w-4 h-4" />
                            {lake.website}
                          </a>
                        )}
                        {lake.phone && (
                          <a href={`tel:${lake.phone}`} className="flex items-center gap-2 text-[#8a948a] hover:text-white">
                            <Phone className="w-4 h-4" />
                            {lake.phone}
                          </a>
                        )}
                        {lake.email && (
                          <a href={`mailto:${lake.email}`} className="flex items-center gap-2 text-[#8a948a] hover:text-white">
                            <Mail className="w-4 h-4" />
                            {lake.email}
                          </a>
                        )}
                      </div>
                    </div>
                  )}
                </TabsContent>

                {/* Fish & Records Tab */}
                <TabsContent value="fish" className="space-y-6 mt-6">
                  {/* Fish Types */}
                  <div className="glass-card p-6">
                    <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-wider text-white mb-4">
                      Fish Species
                    </h2>
                    <div className="flex flex-wrap gap-2">
                      {lake.fish_types?.map(type => (
                        <span key={type} className="flex items-center gap-2 bg-[#141714] text-[#c8b273] px-3 py-2 rounded">
                          <Fish className="w-4 h-4" />
                          {type}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Pro-locked upgrade card (shown to Basic/anon when backend stripped Pro fields) */}
                  {lake.pro_locked && (
                    <ProLockedCard
                      title="Famous Fish & Lake Records"
                      description="Upgrade to Pro to unlock the named fish list, lake records and detailed water intel (depth, stock level, total swims)."
                      testId="lake-pro-locked"
                    />
                  )}

                  {/* Named Fish */}
                  {lake.named_fish?.length > 0 && (
                    <div className="glass-card p-6">
                      <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-wider text-white mb-4 flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-[#c8b273]" />
                        Famous Named Fish
                      </h2>
                      <div className="space-y-4">
                        {displayedNamedFish.map((fish, idx) => (
                          <motion.div 
                            key={idx}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: idx * 0.1 }}
                            className="bg-[#141714] rounded-lg p-4 border border-white/5"
                          >
                            <div className="flex items-start justify-between">
                              <div>
                                <h3 className="text-lg font-bold text-white">{fish.name}</h3>
                                <p className="text-[#c8b273] font-semibold">{fish.species} - {fish.weight}lb</p>
                              </div>
                              {fish.times_caught && (
                                <Badge variant="outline" className="border-white/20 text-[#8a948a]">
                                  Caught {fish.times_caught}x
                                </Badge>
                              )}
                            </div>
                            {fish.description && (
                              <p className="text-[#8a948a] text-sm mt-2">{fish.description}</p>
                            )}
                            {fish.last_caught && (
                              <p className="text-xs text-[#4a504a] mt-2">Last caught: {fish.last_caught}</p>
                            )}
                          </motion.div>
                        ))}
                        {lake.named_fish.length > 3 && (
                          <Button
                            variant="ghost"
                            onClick={() => setShowAllNamedFish(!showAllNamedFish)}
                            className="w-full text-[#c8b273]"
                          >
                            {showAllNamedFish ? (
                              <>Show Less <ChevronUp className="w-4 h-4 ml-1" /></>
                            ) : (
                              <>Show All {lake.named_fish.length} Fish <ChevronDown className="w-4 h-4 ml-1" /></>
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Lake Records */}
                  {lake.lake_records?.length > 0 && (
                    <div className="glass-card p-6">
                      <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-wider text-white mb-4 flex items-center gap-2">
                        <Award className="w-5 h-5 text-[#ff5e00]" />
                        Lake Records
                      </h2>
                      <div className="space-y-3">
                        {lake.lake_records.map((record, idx) => (
                          <div key={idx} className="flex items-center justify-between bg-[#141714] rounded-lg p-3">
                            <div>
                              <p className="text-white font-semibold">{record.species}</p>
                              {record.angler && <p className="text-xs text-[#8a948a]">by {record.angler}</p>}
                            </div>
                            <div className="text-right">
                              <p className="text-[#ff5e00] font-bold text-xl">{record.weight}lb</p>
                              {record.bait && <p className="text-xs text-[#8a948a]">{record.bait}</p>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </TabsContent>

                {/* Catches Tab */}
                <TabsContent value="catches" className="space-y-6 mt-6">
                  <div className="flex items-center justify-between">
                    <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-wider text-white">
                      Recent Catches
                    </h2>
                    <Dialog open={catchDialogOpen} onOpenChange={setCatchDialogOpen}>
                      <DialogTrigger asChild>
                        <Button className="btn-gold" data-testid="log-catch-btn">
                          <Plus className="w-4 h-4 mr-2" />
                          Log Catch
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="bg-[#0b0d0b] border-white/10">
                        <DialogHeader>
                          <DialogTitle className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-wider text-white">
                            Log Your Catch
                          </DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleLogCatch} className="space-y-4 mt-4">
                          <div>
                            <label className="tactical-label">Fish Type</label>
                            <Select value={catchForm.fish_type} onValueChange={(v) => setCatchForm(p => ({ ...p, fish_type: v }))}>
                              <SelectTrigger className="bg-[#141714] border-white/10 text-white">
                                <SelectValue placeholder="Select fish type" />
                              </SelectTrigger>
                              <SelectContent className="bg-[#0b0d0b] border-white/10">
                                {fishTypes.map(type => (
                                  <SelectItem key={type} value={type}>{type}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="tactical-label">Weight (lb)</label>
                              <Input
                                type="number"
                                step="0.1"
                                value={catchForm.weight}
                                onChange={(e) => setCatchForm(p => ({ ...p, weight: e.target.value }))}
                                className="bg-[#141714] border-white/10 text-white"
                                required
                              />
                            </div>
                            <div>
                              <label className="tactical-label">Length (inches)</label>
                              <Input
                                type="number"
                                step="0.1"
                                value={catchForm.length}
                                onChange={(e) => setCatchForm(p => ({ ...p, length: e.target.value }))}
                                className="bg-[#141714] border-white/10 text-white"
                              />
                            </div>
                          </div>
                          <div>
                            <label className="tactical-label">Bait Used</label>
                            <Input
                              value={catchForm.bait_used}
                              onChange={(e) => setCatchForm(p => ({ ...p, bait_used: e.target.value }))}
                              className="bg-[#141714] border-white/10 text-white"
                              placeholder="e.g., Boilies, Sweetcorn"
                            />
                          </div>
                          <div>
                            <label className="tactical-label">Notes</label>
                            <Textarea
                              value={catchForm.notes}
                              onChange={(e) => setCatchForm(p => ({ ...p, notes: e.target.value }))}
                              className="bg-[#141714] border-white/10 text-white"
                              placeholder="Any additional details..."
                            />
                          </div>
                          <Button type="submit" className="btn-gold w-full">Log Catch</Button>
                        </form>
                      </DialogContent>
                    </Dialog>
                  </div>
                  
                  <div className="space-y-4">
                    {catches.length === 0 ? (
                      <div className="glass-card p-8 text-center">
                        <Fish className="w-12 h-12 text-[#4a504a] mx-auto mb-4" />
                        <p className="text-[#8a948a]">No catches logged yet. Be the first!</p>
                      </div>
                    ) : (
                      catches.map((catch_, index) => (
                        <motion.div
                          key={catch_.id}
                          className="glass-card p-4"
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.05 }}
                        >
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="text-white font-bold">{catch_.username}</p>
                              <p className="text-[#c8b273] font-['Barlow_Condensed'] text-lg font-bold uppercase">
                                {catch_.fish_type} - {catch_.weight}lb
                              </p>
                              {catch_.bait_used && (
                                <p className="text-[#8a948a] text-sm mt-1">Bait: {catch_.bait_used}</p>
                              )}
                            </div>
                            <span className="text-[#4a504a] text-sm">
                              {new Date(catch_.caught_at).toLocaleDateString()}
                            </span>
                          </div>
                        </motion.div>
                      ))
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Pricing Card */}
              <div className="glass-card p-6">
                <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-wider text-white mb-4">
                  Session Prices
                </h2>
                <div className="space-y-3">
                  {lake.day_ticket_price && (
                    <div className="flex justify-between items-center">
                      <span className="text-[#8a948a]">Day Session</span>
                      <span className="text-white font-bold">£{lake.day_ticket_price}</span>
                    </div>
                  )}
                  {lake.night_ticket_price && (
                    <div className="flex justify-between items-center">
                      <span className="text-[#8a948a]">Night Session</span>
                      <span className="text-white font-bold">£{lake.night_ticket_price}</span>
                    </div>
                  )}
                  {lake.session_24hr_price && (
                    <div className="flex justify-between items-center">
                      <span className="text-[#8a948a]">24hr Session</span>
                      <span className="text-white font-bold">£{lake.session_24hr_price}</span>
                    </div>
                  )}
                  {lake.session_48hr_price && (
                    <div className="flex justify-between items-center">
                      <span className="text-[#8a948a]">48hr Session</span>
                      <span className="text-white font-bold">£{lake.session_48hr_price}</span>
                    </div>
                  )}
                </div>
                {user?.is_admin ? (
                  <Button
                    className="btn-gold w-full mt-4"
                    onClick={() => setBookingDialogOpen(true)}
                  >
                    <Calendar className="w-4 h-4 mr-2" />
                    Book Now
                    <span className="ml-2 text-[10px] uppercase tracking-widest text-black/70 border border-black/30 rounded px-1.5 py-0.5">Admin</span>
                  </Button>
                ) : (
                  <div className="mt-4">
                    <BookingComingSoon
                      lakeId={id}
                      lakeName={lake.name}
                      testId="book-now-cta"
                    />
                    <p className="text-xs text-[#6b7d6b] text-center mt-2 leading-relaxed">
                      Prices shown are fishery rates for reference. Direct booking is rolling out as partner fisheries come online.
                    </p>
                  </div>
                )}
              </div>

              {/* Community photo submission CTA */}
              {token && (
                <SubmitLakePhotoCTA
                  lakeId={id}
                  lakeName={lake.name}
                  token={token}
                  variant="card"
                />
              )}

              {/* Reviews */}
              <div className="glass-card p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-wider text-white">
                    Reviews
                  </h2>
                  <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
                    <DialogTrigger asChild>
                      <Button variant="ghost" className="text-[#c8b273] text-sm p-0" data-testid="add-review-btn">
                        Add Review
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-[#0b0d0b] border-white/10">
                      <DialogHeader>
                        <DialogTitle className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-wider text-white">
                          Add Review
                        </DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleAddReview} className="space-y-4 mt-4">
                        <div>
                          <label className="tactical-label">Rating</label>
                          <div className="flex gap-2 mt-2">
                            {[1, 2, 3, 4, 5].map(num => (
                              <button
                                key={num}
                                type="button"
                                onClick={() => setReviewForm(p => ({ ...p, rating: num }))}
                                className={`w-10 h-10 rounded ${reviewForm.rating >= num ? 'bg-[#c8b273] text-black' : 'bg-[#141714] text-[#4a504a]'}`}
                              >
                                <Star className="w-5 h-5 mx-auto fill-current" />
                              </button>
                            ))}
                          </div>
                        </div>
                        <div>
                          <label className="tactical-label">Comment</label>
                          <Textarea
                            value={reviewForm.comment}
                            onChange={(e) => setReviewForm(p => ({ ...p, comment: e.target.value }))}
                            className="bg-[#141714] border-white/10 text-white"
                            placeholder="Share your experience..."
                          />
                        </div>
                        <Button type="submit" className="btn-gold w-full">Submit Review</Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
                
                <div className="space-y-4">
                  {reviews.length === 0 ? (
                    <p className="text-[#8a948a] text-center py-4">No reviews yet</p>
                  ) : (
                    reviews.slice(0, 5).map((review) => (
                      <div key={review.id} className="border-b border-white/5 pb-4 last:border-0">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="flex">
                            {[1, 2, 3, 4, 5].map(num => (
                              <Star
                                key={num}
                                className={`w-4 h-4 ${num <= review.rating ? 'text-[#c8b273] fill-current' : 'text-[#4a504a]'}`}
                              />
                            ))}
                          </div>
                          <span className="text-white text-sm font-bold">{review.username}</span>
                        </div>
                        {review.comment && (
                          <p className="text-[#8a948a] text-sm">{review.comment}</p>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Lake Info */}
              {(lake.max_depth_ft || lake.stock_level) && (
                <div className="glass-card p-6">
                  <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-wider text-white mb-4">
                    Lake Info
                  </h2>
                  <div className="space-y-3">
                    {lake.max_depth_ft && (
                      <div className="flex justify-between">
                        <span className="text-[#8a948a]">Max Depth</span>
                        <span className="text-white">{lake.max_depth_ft}ft</span>
                      </div>
                    )}
                    {lake.stock_level && (
                      <div className="flex justify-between">
                        <span className="text-[#8a948a]">Stock Level</span>
                        <span className="text-white">{lake.stock_level}</span>
                      </div>
                    )}
                    {lake.allows_night_fishing !== undefined && (
                      <div className="flex justify-between">
                        <span className="text-[#8a948a]">Night Fishing</span>
                        <span className={lake.allows_night_fishing ? 'text-green-500' : 'text-red-500'}>
                          {lake.allows_night_fishing ? 'Yes' : 'No'}
                        </span>
                      </div>
                    )}
                    {lake.booking_required !== undefined && (
                      <div className="flex justify-between">
                        <span className="text-[#8a948a]">Booking Required</span>
                        <span className={lake.booking_required ? 'text-[#c8b273]' : 'text-green-500'}>
                          {lake.booking_required ? 'Yes' : 'No'}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Achievement celebration */}
      <BadgeUnlockModal
        badges={unlockedBadges}
        onClose={() => setUnlockedBadges([])}
      />
    </div>
  );
};

export default LakeDetail;
